import { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Download, ExpandMore, Sync } from '@mui/icons-material';
import { SectionLabel } from '@/common/components/ui';
import { VersionHistoryButton } from '@/common/components/ui/VersionHistoryButton';
import type { ProjectVersionType } from '@/generated/graphql';
import { useProjectDetails } from '../context/ProjectDetailsContext';
import FileChangeChips from '../tabs/projectVersionHistory/components/FileChangeChips';
import SyncConfirmDialog from '../tabs/projectVersionHistory/components/SyncConfirmDialog';
import {
  useProjectVersionSync,
  type SyncDialogData,
} from '../tabs/projectVersionHistory/hooks/useProjectVersionSync';

function VersionRow({
  version,
  showSyncDialog,
}: {
  version: ProjectVersionType;
  showSyncDialog: (data: SyncDialogData) => void;
}) {
  const {
    project,
    selectedVersion,
    setSelectedVersion,
    latestVersionNumber,
    syncProjectDir,
  } = useProjectDetails();
  const [expanded, setExpanded] = useState(false);
  const selected = selectedVersion === version.version;
  const {
    latestVersionData,
    latestVersionLoading,
    previousVersionData,
    versionSyncStatus,
    handleDownloadVersion,
    handleSyncToVersion,
  } = useProjectVersionSync({
    project: project!,
    version,
    isExpandedVersion: selected && expanded,
    projectDir: syncProjectDir,
    showSyncDialog,
  });

  return (
    <Box
      component="li"
      data-version={version.version}
      data-selected={selected}
      sx={{ listStyle: 'none', borderBottom: 1, borderColor: 'divider' }}
    >
      <VersionHistoryButton
        version={version}
        selected={selected}
        latest={version.version === latestVersionNumber}
        onSelect={() => setSelectedVersion(version.version)}
      />
      <Collapse in={selected}>
        <Box sx={{ px: 3, py: 2.5 }}>
          <Stack direction="row" gap={1.5} sx={{ mb: 2.5 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Download />}
              onClick={handleDownloadVersion}
              sx={{ flex: 1, minHeight: 36 }}
            >
              Download
            </Button>
            <Tooltip
              title={`Replace local files with version ${version.version}. You will be asked to confirm.`}
            >
              <Box component="span" sx={{ flex: 1, display: 'flex' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Sync />}
                  onClick={handleSyncToVersion}
                  disabled={versionSyncStatus.syncing}
                  data-cy="version-sync-button"
                  fullWidth
                  sx={{ minHeight: 36 }}
                >
                  {versionSyncStatus.syncing ? 'Syncing…' : 'Sync'}
                </Button>
              </Box>
            </Tooltip>
          </Stack>
          <Button
            size="small"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
            aria-controls={`page-version-changes-${version._id}`}
            endIcon={
              <ExpandMore
                sx={{ transform: expanded ? 'rotate(180deg)' : undefined }}
              />
            }
            fullWidth
            sx={{
              px: 0,
              pt: 2,
              pb: 1.5,
              mb: 0.5,
              borderTop: 1,
              borderColor: 'divider',
              borderRadius: 0,
              justifyContent: 'space-between',
              color: 'text.secondary',
            }}
          >
            {expanded ? 'Hide file changes' : 'Inspect file changes'}
          </Button>
          {expanded && latestVersionLoading && (
            <LinearProgress sx={{ mb: 1 }} />
          )}
          <Box id={`page-version-changes-${version._id}`}>
            <FileChangeChips
              version={version}
              isActuallyExpanded={expanded && !!latestVersionData}
              latestVersionData={latestVersionData}
              previousVersionData={previousVersionData}
            />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

/** Version selection and verified transfer operations for the project page. */
export default function ProjectVersionRail({
  variant = 'rail',
}: {
  variant?: 'rail' | 'tab';
}) {
  const { project } = useProjectDetails();
  const [dialogData, setDialogData] = useState<SyncDialogData | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const versions = [...(project?.versionHistory || [])].sort(
    (a, b) => b.version - a.version
  );

  const confirmSync = async () => {
    if (!dialogData) return;
    setIsSyncing(true);
    try {
      await dialogData.onConfirmSync();
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setIsSyncing(false);
      setDialogData(null);
    }
  };

  return (
    <Box
      component={variant === 'rail' ? 'aside' : 'section'}
      aria-label="Project version history"
      sx={{
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: variant === 'rail' ? '1px solid' : 0,
        borderColor: 'divider',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 3,
          minHeight: 56,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <SectionLabel>Version history · {versions.length}</SectionLabel>
      </Stack>
      <Box
        id="project-page-versions"
        role="region"
        aria-label="Versions"
        tabIndex={0}
        sx={{
          overflowY: variant === 'rail' ? 'auto' : 'visible',
          minHeight: 0,
          overscrollBehaviorY: 'contain',
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: -2,
          },
        }}
      >
        {versions.length ? (
          <Box component="ul" sx={{ m: 0, p: 0 }}>
            {versions.map((version) => (
              <VersionRow
                key={version._id}
                version={version}
                showSyncDialog={setDialogData}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2.5 }}>
            No version history yet. Create your first version by uploading files
            and syncing.
          </Typography>
        )}
      </Box>
      <SyncConfirmDialog
        open={!!dialogData}
        onClose={() => {
          if (!isSyncing) setDialogData(null);
        }}
        onConfirm={() => {
          void confirmSync();
        }}
        versionNumber={dialogData?.version.version || 0}
        newerFiles={dialogData?.newerFiles || 0}
        behindFiles={dialogData?.behindFiles || 0}
        deletedFiles={dialogData?.deletedFiles || 0}
        newFiles={dialogData?.newFiles || 0}
        isSyncing={isSyncing}
      />
    </Box>
  );
}
